// Ball in hand: placing the cue ball in the D with W A S D (or the touch screen's W A S D pad), and showing the opponent where it is (game:cue-move).
//
// KNOWN BUG (not fixed here): the opponent never gets the final cue-ball-in-hand position. moveCueBallInHand sends
// game:cue-move at most every CUE_MOVE_SEND_MS (80 ms) and nothing is sent after the keys are released, so at 60 fps
// the opponent's view can stop up to ~80 ms of movement short (the shot itself uses the agreed position, the
// shot-start's cueStart). See TESTING.md, cue-ball-in-hand.js.
import Balls from "../Balls";
import {clampToD, defaultCuePosition, inD, isFree, Positions, Vec} from "../shared/rules";
import {engine} from "./engine";
import {Net} from "./network";
import type {MatchCore} from "./types";
import type {CueMoveMsg} from "./messages";

export const HAND_SPEED = 2.4;        // ball-in-hand movement, table units per second
export const MAX_HAND_DT = 0.1;       // s: a long frame never moves the ball further than this much time would
export const CUE_MOVE_SEND_MS = 80;   // game:cue-move at most this often

const keys: Record<string, boolean> = {};
// the touch screen's on-screen W A S D pad (components/Hud/TouchControls.tsx) presses the same four keys
const touchKeys: Record<string, boolean> = {};

export const HAND_KEYS = ["KeyW", "KeyA", "KeyS", "KeyD"] as const;
export type HandKey = typeof HAND_KEYS[number];

/** Touch: an on-screen pad button is held (down) or let go. */
export function setTouchHandKey(code: HandKey, down: boolean) { touchKeys[code] = down; }
export function releaseTouchHandKeys() { for (const k of HAND_KEYS) touchKeys[k] = false; }

/** Keyboard and touch pad together: a key counts as held if either holds it. */
export function mergeKeys(a: Record<string, boolean>, b: Record<string, boolean>): Record<string, boolean> {
    const out: Record<string, boolean> = {};
    for (const k of HAND_KEYS) out[k] = !!(a[k] || b[k]);
    return out;
}

/** Match.init: track the W A S D keys. */
export function listenForKeys(doc: Document = document) {
    doc.addEventListener("keydown", e => { keys[e.code] = true; }, false);
    doc.addEventListener("keyup", e => { keys[e.code] = false; }, false);
}

/** The cue ball is in hand where it is: on the table (current not null) and inside the D. */
export function isPlacedInD(current: Vec | null): boolean {
    return !!current && inD(current, 0.01);
}

/** Where to put a cue ball that isn't in the D: the rules' position if it is valid and free, else the default spot. */
export function inHandTarget(wanted: Vec | undefined, others: Positions): Vec {
    return wanted && inD(wanted, 0.01) && isFree(others, wanted) ? wanted : defaultCuePosition(others);
}

/** W A S D → (right, forward) in -1..1 */
export function keyDirection(k: Record<string, boolean>): {fx: number, fy: number} {
    let fx = 0, fy = 0;
    if (k.KeyW) fy += 1;
    if (k.KeyS) fy -= 1;
    if (k.KeyD) fx += 1;
    if (k.KeyA) fx -= 1;
    return {fx, fy};
}

/** Distance to move this frame: time-based, 2.4 units/s (= the old 0.04 per frame at 60 fps). */
export function handStep(now: number, lastMove: number): number {
    const dt = Math.min(MAX_HAND_DT, Math.max(0, (now - lastMove) / 1000));
    return HAND_SPEED * dt;
}

/**
 * Where the cue ball moves to from p, relative to the camera angle h, or null if it can't move.
 * Tries the full move, then sliding along each axis, so a ball or the D edge never blocks every key.
 */
export function handMoveTarget(p: Vec, h: number, dir: {fx: number, fy: number}, step: number, others: Positions): Vec | null {
    const {fx, fy} = dir;
    // forward = from the camera towards the cue ball; right = forward rotated -90°
    const fwd: Vec = [-Math.sin(h), -Math.cos(h)];
    const right: Vec = [Math.cos(h), -Math.sin(h)];
    const dx = (fwd[0] * fy + right[0] * fx) * step, dy = (fwd[1] * fy + right[1] * fx) * step;
    const options: Vec[] = [clampToD([p[0] + dx, p[1] + dy]), clampToD([p[0] + dx, p[1]]), clampToD([p[0], p[1] + dy])];
    return options.find(t => isFree(others, t) && (Math.abs(t[0] - p[0]) > 1e-6 || Math.abs(t[1] - p[1]) > 1e-6)) ?? null;
}

function othersThanCue(): Positions {
    const others = Balls.positions();
    delete others.cue;
    return others;
}

// ----- called through Match -----

/** Ball in hand: the cue ball must be on the table and inside the D (never left in a pocket). */
export function ensureCueInHand(m: MatchCore) {
    const cue = Balls.byId.cue;
    if (!cue) return;
    const p = cue.Killed || !cue.PhysicalBody ? null : cue.PhysicalBody.GetPosition();
    if (isPlacedInD(p ? [p.x, p.y] : null)) return;
    Balls.show(cue, inHandTarget(m.frame.positions.cue, othersThanCue()));
}

export function moveCueBallInHand(m: MatchCore) {
    const now = performance.now();
    const step = handStep(now, m.lastHandMove);
    m.lastHandMove = now;
    const dir = keyDirection(mergeKeys(keys, touchKeys));
    if (!dir.fx && !dir.fy) return;
    const h = engine().game.Camera.horizontalAngle;
    const cue = Balls.byId.cue;
    const p = cue.PhysicalBody.GetPosition();
    const target = handMoveTarget([p.x, p.y], h, dir, step, othersThanCue());
    if (!target) return;
    Balls.show(cue, target);
    if (m.mode === "online" && m.online && performance.now() - m.lastCueMoveSent > CUE_MOVE_SEND_MS) {
        m.lastCueMoveSent = performance.now();
        Net.cueMove({gameId: m.online.gameId, position: target});
    }
}

/** socket "game:cue-move": the opponent is placing the cue ball. */
export function onCueMove(m: MatchCore, msg: CueMoveMsg) {
    if (!m.online || msg.gameId !== m.online.gameId || m.myTurn()) return;
    if (Array.isArray(msg.position)) Balls.show(Balls.byId.cue, msg.position);
}
